import {
  BookInput,
  DeliveryMethod,
  LabelFileType,
  LabelIssueType,
  LineInput,
  PackingOrderType,
  PackingQueueRowType,
  PackingSlipType,
  PrinterType,
  RebookInput,
  ShipmentSettingsType,
  ShipmentType,
  ShippingPriceInput,
  ShippingPriceType,
  UnmatchedShipmentType,
} from "types/ShippingTypes";
import { API_URL, throwFetchError } from "./common";

const BASE = `${API_URL}/v1/ecommerce/shipping`;

/** Every shipping route answers errors with a plain-text message; this
 *  surfaces it as the FetchError message. A 204 resolves to undefined. */
const request = <T>(
  token: string,
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: unknown
): Promise<T> =>
  fetch(`${BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) {
      return throwFetchError(res);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return res.json();
  });

// --- Packing ------------------------------------------------------------------

export const apiGetPackingQueue = (
  token: string,
  deliveryMethod?: DeliveryMethod
): Promise<PackingQueueRowType[]> =>
  request(
    token,
    deliveryMethod ? `/packing?deliveryMethod=${deliveryMethod}` : "/packing"
  );

export const apiGetPackingOrder = (
  token: string,
  orderId: number
): Promise<PackingOrderType> => request(token, `/packing/${orderId}`);

export const apiBookShipment = (
  token: string,
  orderId: number,
  body: BookInput
): Promise<ShipmentType> =>
  request(token, `/packing/${orderId}/book`, "POST", body);

export const apiReadyForPickup = (
  token: string,
  orderId: number,
  lines: LineInput[]
): Promise<ShipmentType> =>
  request(token, `/packing/${orderId}/ready`, "POST", { lines });

export const apiHandedToDriver = (
  token: string,
  orderId: number,
  lines: LineInput[]
): Promise<ShipmentType> =>
  request(token, `/packing/${orderId}/handed`, "POST", { lines });

// --- Shipments ----------------------------------------------------------------

export const apiGetShipments = (
  token: string,
  filter: { state?: string; orderId?: number; limit?: number } = {}
): Promise<ShipmentType[]> => {
  const params = new URLSearchParams();
  if (filter.state) params.set("state", filter.state);
  if (filter.orderId) params.set("orderId", String(filter.orderId));
  if (filter.limit) params.set("limit", String(filter.limit));
  const query = params.toString();
  return request(token, query ? `/shipments?${query}` : "/shipments");
};

export const apiCancelShipment = (
  token: string,
  shipmentId: number
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/cancel`, "POST");

export const apiRebookShipment = (
  token: string,
  shipmentId: number,
  body: RebookInput
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/rebook`, "POST", body);

export const apiMarkCollected = (
  token: string,
  shipmentId: number
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/collected`, "POST");

export const apiMarkSent = (
  token: string,
  shipmentId: number
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/mark-sent`, "POST");

export const apiReprintLabel = (
  token: string,
  shipmentId: number
): Promise<{ sent: boolean }> =>
  request(token, `/shipments/${shipmentId}/reprint`, "POST");

export const apiGetLabel = (
  token: string,
  shipmentId: number
): Promise<LabelFileType> => request(token, `/shipments/${shipmentId}/label`);

export const apiGetPackingSlip = (
  token: string,
  shipmentId: number
): Promise<PackingSlipType> =>
  request(token, `/shipments/${shipmentId}/packing-slip`);

export const apiUpdateOwnDeliveryCost = (
  token: string,
  shipmentId: number,
  cost: number
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/own-delivery-cost`, "PUT", {
    cost,
  });

export const apiAssignShipmentLines = (
  token: string,
  shipmentId: number,
  lines: LineInput[]
): Promise<ShipmentType> =>
  request(token, `/shipments/${shipmentId}/lines`, "PUT", { lines });

export const apiGetUnmatched = (
  token: string
): Promise<UnmatchedShipmentType[]> => request(token, "/unmatched");

export const apiAssignUnmatched = (
  token: string,
  shipmentId: number,
  orderNo: string
): Promise<ShipmentType> =>
  request(token, `/unmatched/${shipmentId}/assign`, "POST", { orderNo });

// --- Label issues -------------------------------------------------------------

export const apiGetLabelIssues = (
  token: string,
  state: "open" | "all" | "cleared" | "ok" = "open",
  orderId?: number
): Promise<LabelIssueType[]> =>
  request(
    token,
    orderId
      ? `/label-issues?state=${state}&orderId=${orderId}`
      : `/label-issues?state=${state}`
  );

export const apiMarkLabelIssueOk = (
  token: string,
  issueId: number,
  note: string
): Promise<LabelIssueType> =>
  request(token, `/label-issues/${issueId}/ok`, "POST", { note });

// --- Prices -------------------------------------------------------------------

export const apiGetShippingPrices = (
  token: string
): Promise<ShippingPriceType[]> => request(token, "/prices");

export const apiCreateShippingPrice = (
  token: string,
  body: ShippingPriceInput
): Promise<ShippingPriceType> => request(token, "/prices", "POST", body);

export const apiUpdateShippingPrice = (
  token: string,
  priceId: number,
  body: Partial<ShippingPriceInput>
): Promise<ShippingPriceType> =>
  request(token, `/prices/${priceId}`, "PUT", body);

export const apiDeleteShippingPrice = (
  token: string,
  priceId: number
): Promise<void> => request(token, `/prices/${priceId}`, "DELETE");

// --- Settings -----------------------------------------------------------------

// The settings row the shipment settings page edits.
export const SHIPMENT_SETTING = "SHIPMENT";

export const apiGetShipmentSettings = (
  token: string
): Promise<ShipmentSettingsType> => request(token, "/settings");

export const apiSaveShipmentSettings = (
  token: string,
  settings: ShipmentSettingsType
): Promise<ShipmentSettingsType> =>
  request(token, "/settings", "PUT", settings);

export const apiGetPrinters = (token: string): Promise<PrinterType[]> =>
  request(token, "/printers");
